import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Injectable, inject } from '@angular/core';
import { mergeMap, tap, withLatestFrom } from 'rxjs';
import { navOrdAutomaticShuffleUrl, navOrdConfirmPickupOrRelocateUrl, navOrdConfirmRelocateUrl, navOrdPickUpReservedUrl, navOrdRelocateUrl, navOrdUrl } from 'src/app/shared/routing/routing.const';

import { OrdOrderI } from '../ord-search/models/ord-order.interface';
import { AmqModeEnum } from 'src/app/shared/services/activeMQ/models/amq-mode.enum';
import { AmqMoveActions } from './amq-move.actions';
import { AmqSocketActions } from '../amq-socket/amq-socket.actions';
import { AmqTriggerActions } from '../amq-trigger/amq-trigger.actions';
import { NavController } from '@ionic/angular';
import { SpxStorage, SpxStorageKeyEnum } from '@softpak/components/spx-storage';
import { Store } from '@ngrx/store';
import { statsActionActions } from '../stats-action';
import ordDetailsReducer from '../ord-details/ord-details.reducer';

@Injectable()
export class AmqMoveEffects {
    private readonly actions$ = inject(Actions);
    private readonly store = inject(Store);
    private readonly navController = inject(NavController);

    private readonly _askPickUpOrRelocate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AmqMoveActions.askPickUpOrRelocate),
            tap((action) => this.navController.navigateRoot(navOrdConfirmPickupOrRelocateUrl(action.pickUpOrder.transactionNumber, action.relocateOrder ? action.relocateOrder.orderSequenceNumber : 0, action.relocateOrder ? action.relocateOrder.orderLineSequenceNumber : 0))),
            mergeMap((action) => [
                this.createStatsAction('AMQ_ASK_PICKUP_OR_RELOCATE', {
                    ...this.createOrderStatsData(action.pickUpOrder, 'pickUp'),
                    ...this.createOrderStatsData(action.relocateOrder, 'relocate'),
                }),
                // AmqSocketActions.log({ ask: true }),
            ])
        )
    );

    private readonly _askRelocate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AmqMoveActions.askRelocate),
            tap((action) => this.navController.navigateRoot(navOrdConfirmRelocateUrl(action.order.orderSequenceNumber, action.order.orderLineSequenceNumber))),
            mergeMap((action) => [
                this.createStatsAction('AMQ_ASK_ORDER_RELOCATE', this.createOrderStatsData(action.order)),
                // AmqSocketActions.log({ ask: true }),
            ])
        )
    );

    private readonly _askSqRelocate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AmqMoveActions.askSqRelocate),
            tap((action) => this.navController.navigateRoot(navOrdConfirmRelocateUrl(action.order.orderSequenceNumber, action.order.orderLineSequenceNumber))),
            mergeMap((action) => [
                this.createStatsAction('AMQ_ASK_SQ_RELOCATE', this.createOrderStatsData(action.order)),
                // AmqSocketActions.log({ ask: true }),
            ])
        )
    );

    private readonly _startDropOff$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AmqMoveActions.startDropOff),
            mergeMap((action) => (
                [
                    AmqTriggerActions.submitLiftOffTruck({
                        containerSequenceNumber: action.containerSequenceNumber,
                        orderSequenceNumber: action.orderSequenceNumber,
                    }),
                    // AmqSocketActions.log({ mode: AmqModeEnum.dropOff, start: true }),
                ]
            ))
        )
    );

    private readonly _finishDropOff$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AmqMoveActions.finishDropOff),
            mergeMap((action) => (
                [
                    AmqTriggerActions.submitPlaceInStack({
                        location: action.location,
                        containerSequenceNumber: action.containerSequenceNumber,
                        orderSequenceNumber: action.orderSequenceNumber,
                    }),
                    // AmqSocketActions.log({ mode: AmqModeEnum.dropOff, finish: true }),
                ]
            ))
        )
    );

    private readonly _startPickUp$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AmqMoveActions.startPickup),
            tap((action) => this.navController.navigateRoot(navOrdPickUpReservedUrl(action.order.transactionNumber))),
            mergeMap((action) => [
                this.createStatsAction('AMQ_START_PICKUP', this.createOrderStatsData(action.order)),
                // AmqSocketActions.log({ mode: AmqModeEnum.pickUp, start: true }),
            ])
        )
    );

    private readonly _finishPickUp$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AmqMoveActions.finishPickup),
            mergeMap((action) => (
                [
                    AmqTriggerActions.submitPlaceOnTruck({
                        containerSequenceNumber: action.containerSequenceNumber,
                        orderSequenceNumber: action.orderSequenceNumber,
                    }),
                    // AmqSocketActions.log({ mode: AmqModeEnum.pickUp, finish: true }),
                ]
            ))
        )
    );

    private readonly _startSqRelocate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AmqMoveActions.startSqRelocate),
            tap((action) => this.navController.navigateRoot(navOrdRelocateUrl(action.order.orderSequenceNumber, action.order.orderLineSequenceNumber))),
            mergeMap((action) => (
                [
                    AmqTriggerActions.startRelocate({
                        containerSequenceNumber: action.order.seqNr ?? null,
                        orderSequenceNumber: action.order.orderSequenceNumber ?? null,
                    }),
                    // AmqSocketActions.log({ mode: AmqModeEnum.relocateOrder, start: true }),
                ]
            ))
        )
    );

    private readonly _finishRelocateOrder$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AmqMoveActions.finishRelocateOrder),
            mergeMap((action) => (
                [
                    AmqTriggerActions.submitRelocateOrder({
                        location: action.location,
                        containerSequenceNumber: action.containerSequenceNumber,
                        orderSequenceNumber: action.orderSequenceNumber,
                    }),
                    // AmqSocketActions.log({ mode: AmqModeEnum.relocateOrder, finish: true }),
                ]
            ))
        )
    );

    private readonly _cancelMove$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AmqMoveActions.cancelMove),
            tap(() => this.navController.navigateRoot(navOrdUrl)),
            withLatestFrom(this.store.select(ordDetailsReducer.selectData)),
            mergeMap(([, currentOrder]) => [
                this.createStatsAction('AMQ_CANCEL_MOVE', this.createOrderStatsData(currentOrder)),
                // AmqSocketActions.log({ finish: true }),
            ])
        )
    );

    private readonly _startStandardRelocate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AmqMoveActions.startStandardRelocate),
            tap((action) => { console.log('start standard relocate', navOrdAutomaticShuffleUrl(action.location)); }),
            tap((action) => this.navController.navigateRoot(navOrdAutomaticShuffleUrl(action.location))),
            mergeMap(() => (
                [
                    AmqSocketActions.log({ mode: AmqModeEnum.standard, start: true }),
                ]
            ))
        )
    );

    private readonly _startRelocateFromEmptyLocation$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AmqMoveActions.startRelocateFromEmptyLocation),
            mergeMap((action) => (
                [
                    AmqTriggerActions.startRelocate({
                        containerSequenceNumber: action.containerSequenceNumber,
                        orderSequenceNumber: action.orderSequenceNumber,
                    }),
                    // AmqSocketActions.log({ mode: AmqModeEnum.relocateOrder, start: true }),
                ]
            ))
        )
    );

    private readonly _finishStandardRelocate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AmqMoveActions.finishStandardRelocate),
            mergeMap((action) => (
                [
                    AmqTriggerActions.submitStandardRelocate({
                        location: action.location,
                        containerSequenceNumber: action.containerSequenceNumber,
                    }),
                    // AmqSocketActions.log({ mode: AmqModeEnum.standard, finish: true }),
                ]
            ))
        )
    );

    private readonly _onSetMoveInProgress$ = createEffect(() =>
        this.actions$.pipe(
            ofType(AmqMoveActions.setMoveInProgressBySystem),
            mergeMap(() => (
                [
                    // AmqSocketActions.log({ mode: action.mode, bySystem: true }),
                ]
            ))
        )
    );

    private createOrderStatsData(order?: OrdOrderI | null, prefix = 'order') {
        if (!order) {
            return {};
        }

        return {
            [`${prefix}TransactionNumber`]: order.transactionNumber,
            [`${prefix}SequenceNumber`]: order.seqNr,
            [`${prefix}OrderSequenceNumber`]: order.orderSequenceNumber,
            [`${prefix}OrderLineSequenceNumber`]: order.orderLineSequenceNumber,
            [`${prefix}LocationOrigin`]: order.locationOrigin,
            [`${prefix}ContainerPrefix`]: order.containerPrefix,
            [`${prefix}ContainerNumber`]: order.containerNumber,
        };
    }

    private createStatsAction(action: string, actionData?: Record<string, unknown>) {
        const cleanedActionData = actionData == null
            ? undefined
            : Object.fromEntries(Object.entries(actionData).filter(([, value]) => value != null));

        return statsActionActions.load({
            query: {
                app: 'orders',
                customer: SpxStorage.getSetting(SpxStorageKeyEnum.brand),
                action,
                actionData: cleanedActionData && Object.keys(cleanedActionData).length > 0 ? cleanedActionData : undefined,
                randomDeviceId: SpxStorage.getSetting(SpxStorageKeyEnum.randomDeviceId),
                username: SpxStorage.getSetting(SpxStorageKeyEnum.username),
            },
        });
    }
}
